import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { Grid } from 'semantic-ui-react';
import { useQueryString } from '../hooks/useQueryString';
import { Disabled, Option } from './Option';
import { Segment } from './Segment';

interface DisjunctionRule {
	type: 'disjunction';
	set1: Set<string>;
	set2: Set<string>;
}

type Predicate = {
	property: string;
	values: (string | number | boolean | null)[];
};

type AllowedValuesImplication = {
	property: string;
	allowedValues: (string | number | boolean | null)[];
};

interface ConditionRule {
	type: 'condition';
	predicate: Predicate;
	implication: AllowedValuesImplication;
}

type Rule = DisjunctionRule | ConditionRule;

export type RuleSet = Rule[];

interface OptionsProps {
	options: Record<string, readonly (string | boolean | number)[]>;
	ruleSet?: RuleSet;
	onChange: (options: Record<string, string | boolean | number>) => void;
}

const GridWithoutBorders = styled(Grid)`
	padding: 0 !important;
	margin: 0 !important;
	width: auto !important;

	@media only screen and (max-width: 767px) {
		&.ui.stackable.grid > .column:not(.row) {
			padding: 6px 0px !important;
		}
	}
`;

const Column = styled(Grid.Column)`
	min-width: 412px;
	max-width: 412px;
	padding: 4px 0px !important;
`;

export const Options: React.FC<OptionsProps> = ({ options, onChange, ruleSet }) => {
	const [queryOptions, setQuery] = useQueryString();

	const [optionValues, setOptionValues] = useState(
		Object.keys(options).reduce((agg, curr) => {
			agg[curr] = null;
			return agg;
		}, {} as Record<string, string | boolean | number | null>)
	);

	useEffect(() => {
		const newOptions = {...optionValues, ...queryOptions}; 
		setOptionValues(newOptions);
		onChange(queryOptions);
	}, [queryOptions])

	const handleOptionChange = (key: string) => (value: string | boolean | number | null) => {
		const newOptions = { ...optionValues, [key]: value };
		setOptionValues(newOptions);
		setQuery(newOptions);
		onChange(
			Object.entries(newOptions).reduce((agg, [key, value]) => {
				if (value != null) {
					agg[key] = value;
				}
				return agg;
			}, {} as Record<string, string | boolean | number>)
		);
	};

	const resolveDisjunction = (key: string, rule: DisjunctionRule) => {
		const getRaw = (i: string) => i.split(':')[0];
		const set1Raw = Array.from(rule.set1.values()).map(getRaw);
		const set2Raw = Array.from(rule.set2.values()).map(getRaw);
		const keyRaw = getRaw(key);

		if (!set1Raw.includes(keyRaw) && !set2Raw.includes(keyRaw)) {
			return;
		}

		const clashingOptions = Array.from(
			set2Raw.includes(keyRaw) ? rule.set1.values() : rule.set2.values()
		).filter((otherKey) => optionValues[getRaw(otherKey)]);

		if (clashingOptions.length) {
			const options = clashingOptions.flatMap((c) => c);
			return {
				type: 'option',
				disabledReason: `Option "${key}" cannot be set together with option${
					options.length > 1 ? 's' : ''
				} "${options.join('", "')}"`
			} as Disabled;
		}
	};

	const disjunctionFormat = (values: any[]) => new Intl.ListFormat('en', { style: 'long', type: 'disjunction' }).format(values);

	const resolveConditions = (key: string, rule: ConditionRule) => {
		if (rule.implication.property === key) {
			if (rule.predicate.values.includes(optionValues[rule.predicate.property]) && options[key].length !== rule.implication.allowedValues.length) {
				return {
					type: 'value',
					values: rule.implication.allowedValues,
					disabledReason: `Option "${key}" cannot be set to value "${disjunctionFormat(options[key].filter(f => !rule.implication.allowedValues.includes(f)))}" together with option "${rule.predicate.property}" set to "${disjunctionFormat(rule.predicate.values)}"`
				} as Disabled;
			}
		}
	};

	const getDisabled = (key: string) => {
		const disabledRules = (ruleSet ?? [])
			.map((r) => {
				switch (r.type) {
					case 'disjunction':
						return resolveDisjunction(key, r);
					case 'condition':
						return resolveConditions(key, r);
					default:
						throw new Error('Unknown type');
				}
			})
			.filter(Boolean);
		if (disabledRules.length) {
			return disabledRules[0];
		}
	};

	return (
		<Segment basic>
			<GridWithoutBorders verticalAlign="middle" stackable columns={3}>
				{Object.entries(options).map(([key, value]) => (
					<Column key={key}>
						<Option
							optionKey={key}
							disabled={getDisabled(key)}
							optionValues={value}
							onChange={handleOptionChange(key)}
							selectedValue={optionValues[key]}
						/>
					</Column>
				))}
			</GridWithoutBorders>
		</Segment>
	);
};
